// src/services/api.js - FIXED VERSION untuk mengatasi relativeURL error
import axios from 'axios';
import { tokenStorage } from '../utils/storage';
import { CLEAN_API_BASE_URL } from '../utils/constants';

// Debug logging
console.log('API Service initialized with baseURL:', CLEAN_API_BASE_URL);

// Pastikan baseURL adalah string dan valid
if (!CLEAN_API_BASE_URL || typeof CLEAN_API_BASE_URL !== 'string') {
  console.error('Invalid API_BASE_URL:', CLEAN_API_BASE_URL);
  throw new Error('API_BASE_URL must be a valid string');
}

// Create axios instance dengan konfigurasi yang benar
const api = axios.create({
  baseURL: CLEAN_API_BASE_URL,
  timeout: 30000,
  headers: {
    'Content-Type': 'application/json',
  },
});

// Request interceptor untuk menambahkan token
api.interceptors.request.use(
  (config) => {
    // Debug: log setiap request
    console.log('API Request:', {
      method: config.method?.toUpperCase(),
      url: config.url,
      baseURL: config.baseURL,
      fullURL: `${config.baseURL}${config.url || ''}`,
    });

    // Pastikan URL adalah string
    if (config.url && typeof config.url !== 'string') {
      console.error('Invalid URL type:', typeof config.url, config.url);
      config.url = String(config.url);
    }

    // Tambahkan token jika ada
    const token = tokenStorage.getToken();
    if (token) {
      config.headers.Authorization = `Bearer ${token}`;
    }

    return config;
  },
  (error) => {
    console.error('Request interceptor error:', error);
    return Promise.reject(error);
  }
);

// Response interceptor untuk handle errors dan token refresh
api.interceptors.response.use(
  (response) => {
    // Debug: log response
    console.log('API Response:', {
      url: response.config.url,
      status: response.status,
      statusText: response.statusText,
    });
    return response;
  },
  async (error) => {
    const originalRequest = error.config;

    // Debug: log error
    console.error('API Error:', {
      url: error.config?.url,
      method: error.config?.method,
      status: error.response?.status,
      message: error.message,
      baseURL: error.config?.baseURL,
    });

    // Handle 401 Unauthorized
    if (error.response?.status === 401 && !originalRequest._retry) {
      originalRequest._retry = true;

      // Try to refresh token
      const refreshToken = tokenStorage.getRefreshToken();
      if (refreshToken) {
        try {
          // Gunakan URL absolut untuk refresh token
          const response = await axios.post(`${CLEAN_API_BASE_URL}/auth/refresh`, {
            refreshToken,
          });
          
          const { token } = response.data;
          tokenStorage.setToken(token);
          
          // Retry original request
          originalRequest.headers.Authorization = `Bearer ${token}`;
          return api(originalRequest);
        } catch (refreshError) {
          console.error('Token refresh failed:', refreshError);
          // Refresh failed, redirect to login
          tokenStorage.clearAuth();
          window.location.href = '/admin/login';
          return Promise.reject(refreshError);
        }
      } else {
        // No refresh token, redirect to login
        console.log('No refresh token, redirecting to login');
        tokenStorage.clearAuth();
        window.location.href = '/admin/login';
      }
    }

    return Promise.reject(error);
  }
);

// Generic API methods dengan error handling yang lebih baik
export const apiClient = {
  // GET request
  get: async (url, config = {}) => {
    try {
      // Pastikan URL adalah string
      const cleanUrl = String(url || '');
      console.log('GET request to:', cleanUrl);
      
      const response = await api.get(cleanUrl, config);
      return {
        success: true,
        data: response.data,
        status: response.status,
      };
    } catch (error) {
      console.error('GET request failed:', error);
      return handleApiError(error);
    }
  },

  // POST request
  post: async (url, data = {}, config = {}) => {
    try {
      // Pastikan URL adalah string
      const cleanUrl = String(url || '');
      console.log('POST request to:', cleanUrl, 'with data:', data);
      
      const response = await api.post(cleanUrl, data, config);
      return {
        success: true,
        data: response.data,
        status: response.status,
      };
    } catch (error) {
      console.error('POST request failed:', error);
      return handleApiError(error);
    }
  },

  // PUT request
  put: async (url, data = {}, config = {}) => {
    try {
      const cleanUrl = String(url || '');
      console.log('PUT request to:', cleanUrl);
      
      const response = await api.put(cleanUrl, data, config);
      return {
        success: true,
        data: response.data,
        status: response.status,
      };
    } catch (error) {
      console.error('PUT request failed:', error);
      return handleApiError(error);
    }
  },

  // PATCH request
  patch: async (url, data = {}, config = {}) => {
    try {
      const cleanUrl = String(url || '');
      const response = await api.patch(cleanUrl, data, config);
      return {
        success: true,
        data: response.data,
        status: response.status,
      };
    } catch (error) {
      return handleApiError(error);
    }
  },

  // DELETE request
  delete: async (url, config = {}) => {
    try {
      const cleanUrl = String(url || '');
      const response = await api.delete(cleanUrl, config);
      return {
        success: true,
        data: response.data,
        status: response.status,
      };
    } catch (error) {
      return handleApiError(error);
    }
  },

  // Upload file - FIXED VERSION
  upload: async (url, formData, options = {}) => {
    try {
      const cleanUrl = String(url || '');
      
      // Deteksi method dari options atau default POST
      const method = options.method || 'POST';
      
      const config = {
        headers: {
          'Content-Type': 'multipart/form-data',
        },
      };

      if (options.onUploadProgress) {
        config.onUploadProgress = options.onUploadProgress;
      }

      // Gunakan method yang sesuai
      const response = await api({
        method: method,
        url: cleanUrl,
        data: formData,
        ...config
      });

      return {
        success: true,
        data: response.data,
        status: response.status,
      };
    } catch (error) {
      return handleApiError(error);
    }
  },
};

// Error handler yang sama seperti sebelumnya
const handleApiError = (error) => {
  let errorMessage = 'Terjadi kesalahan pada server';
  let errorCode = 'UNKNOWN_ERROR';
  let validationErrors = {};

  if (error.response) {
    const { status, data } = error.response;
    
    switch (status) {
      case 400:
        errorMessage = data?.message || 'Data yang dikirim tidak valid';
        errorCode = 'BAD_REQUEST';
        if (data?.errors) {
          validationErrors = data.errors;
        }
        break;
      case 401:
        errorMessage = data?.message || 'Tidak memiliki akses';
        errorCode = 'UNAUTHORIZED';
        break;
      case 403:
        errorMessage = data?.message || 'Akses ditolak';
        errorCode = 'FORBIDDEN';
        break;
      case 404:
        errorMessage = data?.message || 'Data tidak ditemukan';
        errorCode = 'NOT_FOUND';
        break;
      case 422:
        errorMessage = data?.message || 'Data validasi gagal';
        errorCode = 'VALIDATION_ERROR';
        if (data?.errors) {
          validationErrors = data.errors;
        }
        break;
      case 429:
        errorMessage = data?.message || 'Terlalu banyak permintaan';
        errorCode = 'TOO_MANY_REQUESTS';
        break;
      case 500:
        errorMessage = data?.message || 'Kesalahan server internal';
        errorCode = 'INTERNAL_SERVER_ERROR';
        break;
      default:
        errorMessage = data?.message || `Error ${status}`;
        errorCode = `HTTP_${status}`;
    }
  } else if (error.request) {
    errorMessage = 'Tidak dapat terhubung ke server';
    errorCode = 'NETWORK_ERROR';
  } else {
    errorMessage = error.message || 'Terjadi kesalahan';
    errorCode = 'REQUEST_ERROR';
  }

  return {
    success: false,
    error: {
      message: errorMessage,
      code: errorCode,
      validationErrors,
      originalError: error,
    },
  };
};

// Export axios instance untuk penggunaan khusus
export default api;